import { useContext, useState, useEffect } from "react";
import {
   Table, Card, message, Form, Button, Modal,
   DatePicker, InputNumber, Tag, Space, Row,
   Col, Input, Select
} from "antd";
import { AuthContext } from "../context/AuthContext";
import dayjs from "dayjs";
import {
   EditOutlined, DollarOutlined, TrophyOutlined,
   CalendarOutlined, UserOutlined, SearchOutlined,
   FilterOutlined, ClearOutlined, RiseOutlined,
   TeamOutlined, CrownOutlined
} from "@ant-design/icons";
import { getAllSales, updateSale } from "../../services/salesApi";

const { Search } = Input;
const { Option } = Select;

const Sales = () => {
   const [sales, setSales] = useState([]);
   const [filteredSales, setFilteredSales] = useState([]);
   const [totalRevenue, setTotalRevenue] = useState(0);
   const [editOpen, setEditOpen] = useState(false);
   const [editingSale, setEditingSale] = useState(null);
   const [loading, setLoading] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);
   const { user } = useContext(AuthContext);
   const [form] = Form.useForm();

   // Filter states
   const [searchText, setSearchText] = useState("");
   const [dateRange, setDateRange] = useState("all");
   const [assignedFilter, setAssignedFilter] = useState("assigned");

   const fetchSales = async () => {
      setLoading(true);
      try {
         const { data } = await getAllSales();
         setSales(data);
         setFilteredSales(data);

         // Calculate total revenue
         const total = data.reduce((sum, item) => {
            return sum + Number(item.sale_amount || 0);
         }, 0);
         setTotalRevenue(total);
      } catch (error) {
         message.error("Failed to load sales");
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchSales();
   }, []);

   // Apply Filters
   useEffect(() => {
      let filtered = [...sales];

      // Search filter
      if (searchText) {
         filtered = filtered.filter(sale =>
            sale.lead_name?.toLowerCase().includes(searchText.toLowerCase()) ||
            sale.assigned_to_name?.toLowerCase().includes(searchText.toLowerCase()) ||
            sale.created_by_name?.toLowerCase().includes(searchText.toLowerCase())
         );
      }

      // Date range filter
      if (dateRange !== "all") {
         const today = dayjs();
         let startDate;

         switch (dateRange) {
            case "today":
               startDate = today.startOf('day');
               break;
            case "week":
               startDate = today.subtract(7, 'day');
               break;
            case "month":
               startDate = today.subtract(30, 'day');
               break;
            case "quarter":
               startDate = today.subtract(90, 'day');
               break;
            default:
               startDate = null;
         }

         if (startDate) {
            filtered = filtered.filter(sale =>
               dayjs(sale.closing_date).isAfter(startDate)
            );
         }
      }

      // Assigned filter
      if (assignedFilter === "assigned") {
         filtered = filtered.filter(sale => sale.assigned_to_name);
      } else if (assignedFilter === "unassigned") {
         filtered = filtered.filter(sale => !sale.assigned_to_name);
      }

      setFilteredSales(filtered);
      setCurrentPage(1);
   }, [searchText, dateRange, assignedFilter, sales]);

   const handleClearFilters = () => {
      setSearchText("");
      setDateRange("all");
      setAssignedFilter("assigned");
   };

   // Calculate stats
   const stats = {
      totalSales: filteredSales.length,
      totalRevenue: filteredSales.reduce((sum, item) => sum + Number(item.sale_amount || 0), 0),
      topPerformer: filteredSales.reduce((acc, sale) => {
         const name = sale.created_by_name;
         if (!acc[name]) acc[name] = 0;
         acc[name] += Number(sale.sale_amount || 0);
         return acc;
      }, {}),
   };

   const topPerformerName = Object.entries(stats.topPerformer)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || "N/A";

   const handleUpdateSale = async (values) => {
      try {
         await updateSale(editingSale.id, {
            sale_amount: values.sale_amount,
            closing_date: values.closing_date.format("YYYY-MM-DD")
         });
         message.success("Sale updated successfully");
         setEditOpen(false);
         fetchSales();
      } catch (err) {
         message.error(err.response?.data?.message || "Update failed");
      }
   };

   const columns = [
      {
         title: "#",
         width: 50,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">
               {(currentPage - 1) * 10 + index + 1}
            </span>
         )
      },
      {
         title: "Lead Name",
         dataIndex: "lead_name",
         render: (name) => (
            <div className="flex items-center gap-2">
               <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <UserOutlined className="text-purple-500 text-xs" />
               </div>
               <span className="font-medium text-gray-800">{name}</span>
            </div>
         )
      },
      {
         title: "Assigned To",
         dataIndex: "assigned_to_name",
         render: (name) => name ? (
            <Tag color="blue" icon={<TeamOutlined />}>
               {name}
            </Tag>
         ) : (
            <Tag color="orange">Unassigned</Tag>
         )
      },
      {
         title: "Sale Amount",
         dataIndex: "sale_amount",
         render: (amount) => (
            <span className="font-semibold text-green-600 text-base">
               ₹ {Number(amount).toLocaleString('en-IN')}
            </span>
         )
      },
      {
         title: "Closing Date",
         dataIndex: "closing_date",
         render: (date) => (
            <div className="flex items-center gap-1">
               <CalendarOutlined className="text-gray-400 text-xs" />
               <span className="text-gray-600">
                  {date ? dayjs(date).format("DD MMM YYYY") : "-"}
               </span>
            </div>
         )
      },
      {
         title: "Converted By",
         dataIndex: "created_by_name",
         render: (name) => (
            <Tag color="green" icon={<CrownOutlined />}>
               {name}
            </Tag>
         )
      },
      ...(user?.role === "admin" ? [{
         title: "Action",
         key: "action",
         width: 100,
         align: "center",
         render: (_, record) => (
            <Button
               type="text"
               icon={<EditOutlined />}
               className="text-blue-500 hover:text-blue-700"
               onClick={() => {
                  setEditingSale(record);
                  form.setFieldsValue({
                     sale_amount: record.sale_amount,
                     closing_date: dayjs(record.closing_date)
                  });
                  setEditOpen(true);
               }}
            />
         )
      }] : [])
   ];

   const hasActiveFilters = searchText || dateRange !== "all" || assignedFilter !== "assigned";

   return (
      <div className="bg-gray-50 min-h-screen">
         <style>{`
            .sales-card {
               background: #fff;
               border-radius: 10px;
               box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03);
               border: 1px solid #f3f4f6;
            }
            .sales-table .ant-table-thead > tr > th {
               background: #f9fafb;
               font-size: 12px;
               font-weight: 600;
               padding: 12px 16px;
            }
            .sales-table .ant-table-tbody > tr > td {
               padding: 12px 16px;
               font-size: 13px;
            }
            .sales-table .ant-table-tbody > tr:hover > td {
               background: #f9fafb;
            }
            .stat-card {
               border-radius: 10px;
               transition: all 0.3s;
            }
            .stat-card:hover {
               transform: translateY(-2px);
               box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            }
         `}</style>

         {/* Header */}
         <div className="bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-xl font-semibold text-gray-800 m-0">Sales Management</h1>
                  <span className="text-gray-400 text-sm">Track and manage all sales conversions</span>
               </div>
               <div className="flex items-center gap-3">
                  <Tag color="green" className="text-sm">
                     <RiseOutlined /> Total Revenue: ₹ {totalRevenue.toLocaleString('en-IN')}
                  </Tag>
               </div>
            </div>
         </div>

         <div className="p-6">
            {/* Stats Cards */}
            <Row gutter={[16, 16]} className="mb-6">
               <Col xs={24} sm={12} lg={user?.role === "admin" ? 8 : 12}>
                  <div className="sales-card stat-card p-4">
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="text-xs text-gray-500 mb-1">Total Sales</div>
                           <div className="text-2xl font-bold text-gray-800">
                              {stats.totalSales}
                           </div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                           <TrophyOutlined className="text-blue-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>

               <Col xs={24} sm={12} lg={user?.role === "admin" ? 8 : 12}>
                  <div className="sales-card stat-card p-4">
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="text-xs text-gray-500 mb-1">Total Revenue</div>
                           <div className="text-2xl font-bold text-green-600">
                              ₹ {stats.totalRevenue.toLocaleString('en-IN')}
                           </div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                           <DollarOutlined className="text-green-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>

               {/* Top Performer - Only for Admin */}
               {/* {user?.role === "admin" && (
                  <Col xs={24} sm={12} lg={8}>
                     <div className="sales-card stat-card p-4">
                        <div className="flex items-center justify-between">
                           <div>
                              <div className="text-xs text-gray-500 mb-1">Top Performer</div>
                              <div className="text-lg font-bold text-gray-800 truncate">
                                 {topPerformerName}
                              </div>
                           </div>
                           <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                              <CrownOutlined className="text-yellow-500 text-lg" />
                           </div>
                        </div>
                     </div>
                  </Col>
               )} */}
            </Row>

            {/* Filters Section */}
            <div className="sales-card p-4 mb-4">
               <div className="flex items-center gap-2 mb-3">
                  <FilterOutlined className="text-gray-600" />
                  <span className="font-medium text-gray-700">Filters</span>
                  {hasActiveFilters && (
                     <Tag color="blue">{filteredSales.length} results</Tag>
                  )}
               </div>

               <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} lg={6}>
                     <Search
                        placeholder="Search by lead or employee"
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        prefix={<SearchOutlined className="text-gray-400" />}
                     />
                  </Col>

                  <Col xs={24} sm={12} lg={5}>
                     <Select
                        value={dateRange}
                        onChange={setDateRange}
                        style={{ width: "100%" }}
                        placeholder="Date range"
                     >
                        <Option value="all">All Time</Option>
                        <Option value="today">Today</Option>
                        <Option value="week">Last 7 Days</Option>
                        <Option value="month">Last 30 Days</Option>
                     </Select>
                  </Col>

                  <Col xs={24} sm={12} lg={4}>
                     <Button
                        icon={<ClearOutlined />}
                        onClick={handleClearFilters}
                        disabled={!hasActiveFilters}
                        block
                     >
                        Clear Filters
                     </Button>
                  </Col>
               </Row>
            </div>

            {/* Sales Table */}
            <div className="sales-card sales-table">
               <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                     <div className="flex items-center gap-2">
                        <DollarOutlined className="text-gray-600" />
                        <span className="font-medium text-gray-700">
                           {hasActiveFilters ? "Filtered Sales" : "All Sales"}
                        </span>
                        <Tag>{filteredSales.length}</Tag>
                     </div>
                     {hasActiveFilters && (
                        <span className="text-xs text-gray-500">
                           Showing {filteredSales.length} of {sales.length} sales
                        </span>
                     )}
                  </div>
               </div>

               <Table
                  dataSource={filteredSales}
                  columns={columns}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                     current: currentPage,
                     onChange: (page) => setCurrentPage(page),
                     pageSize: 10,
                     showTotal: (total, range) => (
                        <span className="text-sm text-gray-500">
                           Showing <b>{range[0]}-{range[1]}</b> of <b>{total}</b> sales
                        </span>
                     ),
                     size: "small"
                  }}
               />
            </div>
         </div>

         {/* Edit Modal */}
         <Modal
            title={
               <div className="flex items-center gap-2">
                  <EditOutlined />
                  <span>Edit Sale Details</span>
               </div>
            }
            open={editOpen}
            onCancel={() => setEditOpen(false)}
            footer={null}
            width={500}
         >
            <Form
               layout="vertical"
               form={form}
               className="mt-4"
               onFinish={handleUpdateSale}
            >
               <Form.Item
                  name="sale_amount"
                  label="Sale Amount"
                  rules={[
                     { required: true, message: "Please enter sale amount" },
                     { type: "number", min: 1, message: "Amount must be greater than 0" }
                  ]}
               >
                  <InputNumber
                     style={{ width: "100%" }}
                     min={1}
                     precision={2}
                     formatter={(value) =>
                        `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                     }
                     parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                     prefix={<DollarOutlined className="text-gray-400" />}
                     placeholder="Enter amount"
                  />
               </Form.Item>

               <Form.Item
                  name="closing_date"
                  label="Closing Date"
                  rules={[{ required: true, message: "Please select closing date" }]}
               >
                  <DatePicker
                     style={{ width: "100%" }}
                     format="DD/MM/YYYY"
                     placeholder="Select date"
                  />
               </Form.Item>

               <Form.Item className="mb-0">
                  <Space className="w-full justify-end">
                     <Button onClick={() => setEditOpen(false)}>
                        Cancel
                     </Button>
                     <Button type="primary" htmlType="submit">
                        Update Sale
                     </Button>
                  </Space>
               </Form.Item>
            </Form>
         </Modal>
      </div>
   );
};

export default Sales;